import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DrfPaginatedResponse } from '../models/api.model';
import { SUPPRESS_ERROR_TOAST } from '../interceptors/http-context';
import {
    CriteresSimilarite,
    PatchedPersonnePhysiqueRequest,
    PersonnePhysique,
    PersonnePhysiqueRequest,
    PersonnePhysiqueSimilaire,
} from '../models/gouvernance/personne-physique.model';
import { buildHttpParams, QueryFilters } from '../../shared/core/services/pagination.util';

/**
 * Filtres serveur de GET /api/v1/personnes-physiques/ effectivement utilisés
 * par les écrans (sous-ensemble des filtres exposés par le backend, noms
 * vérifiés sur l'OpenAPI). Recherche server-side.
 */
export type PersonnePhysiqueListFilters = QueryFilters & {
    page?: number;
    per_page?: number;
    ordering?: string;
    nom_icontains?: string;
    prenoms_icontains?: string;
    email_icontains?: string;
    nni_icontains?: string;
    nationalite_icontains?: string;
    code_identification_icontains?: string;
    /** Enum `M` | `F` — filtre exact. */
    sexe?: string;
};

@Injectable({ providedIn: 'root' })
export class PersonnePhysiqueService {
    private readonly http = inject(HttpClient);
    private readonly endpoint = `${environment.apiUrl}/personnes-physiques/`;

    list(filters: PersonnePhysiqueListFilters = {}): Observable<DrfPaginatedResponse<PersonnePhysique>> {
        return this.http.get<DrfPaginatedResponse<PersonnePhysique>>(this.endpoint, {
            params: buildHttpParams(filters),
        });
    }

    retrieve(id: string): Observable<PersonnePhysique> {
        return this.http.get<PersonnePhysique>(`${this.endpoint}${id}/`);
    }

    create(payload: PersonnePhysiqueRequest): Observable<PersonnePhysique> {
        return this.http.post<PersonnePhysique>(this.endpoint, payload);
    }

    partialUpdate(id: string, payload: PatchedPersonnePhysiqueRequest): Observable<PersonnePhysique> {
        return this.http.patch<PersonnePhysique>(`${this.endpoint}${id}/`, payload);
    }

    destroy(id: string): Observable<void> {
        return this.http.delete<void>(`${this.endpoint}${id}/`);
    }

    /**
     * Personnes déjà enregistrées proches des critères. Lancée pendant la saisie :
     * le toast d'erreur global est coupé, le formulaire signale lui-même une recherche indisponible.
     */
    rechercherSimilaires(criteres: CriteresSimilarite): Observable<DrfPaginatedResponse<PersonnePhysiqueSimilaire>> {
        return this.http.get<DrfPaginatedResponse<PersonnePhysiqueSimilaire>>(`${environment.apiUrl}/similarite-personnes-physiques/`, {
            params: buildHttpParams({ ...criteres, per_page: 10 }),
            context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
        });
    }
}
